import { useEffect, useMemo, useState, useTransition } from "react";

import { graphql, useRefetchableFragment } from "react-relay";
import { useDebouncedCallback } from "use-debounce";

import { createMultipleFilter } from "app/utils/filters";

import {
  buildFilterOperands,
  type FilterFormFields,
} from "../utils/formatNotificationRuleInput";

import type { useResourceMatchCount_query$key } from "./__generated__/useResourceMatchCount_query.graphql";
import type { useResourceMatchCountRefetchQuery } from "./__generated__/useResourceMatchCountRefetchQuery.graphql";

export function useResourceMatchCount(
  queryRef: useResourceMatchCount_query$key,
  filters: FilterFormFields,
) {
  const [isPending, startTransition] = useTransition();
  // True while debounce is pending (filter changed but refetch not yet fired)
  const [isDebouncing, setIsDebouncing] = useState(false);

  const [data, refetch] = useRefetchableFragment<
    useResourceMatchCountRefetchQuery,
    useResourceMatchCount_query$key
  >(
    graphql`
      fragment useResourceMatchCount_query on Query
      @argumentDefinitions(filterElement: { type: "FilterElementInput" })
      @refetchable(queryName: "useResourceMatchCountRefetchQuery") {
        resourceMatches(first: 1, filterElement: $filterElement) {
          pageInfo {
            total
          }
        }
      }
    `,
    queryRef,
  );

  // filterOperands drives both the effect trigger and the "has filters" check.
  // The debounced callback receives filters as an argument to get the latest
  // values at fire time, in case the form changes between schedule and fire.
  const filterOperands = useMemo(() => buildFilterOperands(filters), [filters]);

  const debouncedRefetch = useDebouncedCallback(
    (latestFilters: FilterFormFields) => {
      setIsDebouncing(false);
      const operands = buildFilterOperands(latestFilters);
      if (operands.length === 0) return;
      const filterElement = createMultipleFilter(operands, "AND");
      startTransition(() => {
        refetch({ filterElement });
      });
    },
    500,
  );

  useEffect(() => {
    if (filterOperands.length === 0) {
      setIsDebouncing(false);
      debouncedRefetch.cancel();
      return;
    }
    setIsDebouncing(true);
    debouncedRefetch(filters);
    return () => {
      debouncedRefetch.cancel();
    };
  }, [filterOperands, filters, debouncedRefetch]);

  const hasFilters = filterOperands.length > 0;
  const isLoading = isDebouncing || isPending;
  const matchCount =
    !hasFilters || isLoading
      ? null
      : (data.resourceMatches?.pageInfo?.total ?? null);

  return { matchCount, isLoading, hasFilters };
}
